import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  HostListener,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { Subject, Observable, Subscription, BehaviorSubject } from "rxjs";
import * as _ from "underscore";
import { RootStoreState } from "src/app/root-store";
import { takeUntil, debounceTime, distinctUntilChanged } from "rxjs/operators";

import { ActivatedRoute, Router } from "@angular/router";
import {
  getQuote,
  getQuoteError,
  getQuoteLoading,
  updateQuoteError,
  updateQuoteLoading,
  updateQuoteSuccess,
} from "src/app/root-store/kwikspace-app-store/selectors/quote.selector";
import {
  ClearProductConfiguration,
  ClearProductList,
  ClearUpdateProductConfiguration,
  CreateProductConfiguration,
  GetProductList,
  UpdateProductConfiguration,
} from "src/app/root-store/kwikspace-app-store/actions/product.action";
import {
  ClearCustomerList,
  GetCustomerList,
} from "src/app/root-store/kwikspace-app-store/actions/customer.action";
import {
  getCustomerList,
  getCustomerListError,
  getCustomerListLoading,
} from "src/app/root-store/kwikspace-app-store/selectors/customer.selector";
import {
  getCreatedProductConfigurationError,
  getCreatedProductConfigurationLoading,
  getCreatedProductConfigurationSuccess,
  getProductList,
  getProductListError,
  getProductListLoading,
  updateProductConfigurationError,
  updateProductConfigurationLoading,
  updateProductConfigurationSuccess,
} from "src/app/root-store/kwikspace-app-store/selectors/product.selector";
import { getCurrentUser } from "src/app/root-store/main-app-store/selectors/auth.selector";
import { Product } from "src/app/models/Product.model";
import { Customer } from "src/app/models/Customer.model";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import {
  ClearQuoteList,
  EmailQuote,
  GetQuote,
  UpdateQuote,
} from "src/app/root-store/kwikspace-app-store/actions/quote.action";
import { AppRoutes } from "src/app/shared/endpoints.enum";
import { TanToastrService } from "src/app/services/toastr.service";
import {
  getCreatedLineItemsError,
  getCreatedLineItemsLoading,
  getCreatedLineItemsSuccess,
  getRentalPeriods,
  getRentalPeriodsError,
  getRentalPeriodsLoading,
  getRentalPriceList,
  getRentalPriceListError,
  getRentalPriceListLoading,
  getRentalSizes,
  getRentalSizesError,
  getRentalSizesLoading,
  updateRentalLineItemError,
  updateRentalLineItemLoading,
  updateRentalLineItemSuccess,
} from "src/app/root-store/kwikspace-app-store/selectors/rental.selector";
import {
  ClearRentalLineItems,
  ClearRentalList,
  ClearRentalUpdate,
  CreateRentalLineItem,
  GetRentalList,
  GetRentalPeriod,
  GetRentalPrice,
  GetRentalSize,
  UpdateRentalLineItem,
} from "src/app/root-store/kwikspace-app-store/actions/rental.action";
import { Rental } from "src/app/models/Rental.model";
import {
  ClearTransportAreas,
  ClearTransportDistricts,
  ClearTransportPriceList,
  CreateDiscountedTransportPrice,
  GetTransportAreas,
  GetTransportDistricts,
  GetTransportLoads,
  GetTransportOptions,
  GetTransportPriceList,
  UpdateDiscountedTransportPrice,
} from "src/app/root-store/kwikspace-app-store/actions/transport.action";
import { Transport } from "src/app/models/Transport.model";
import {
  createDiscountedTransportChargeError,
  createDiscountedTransportChargeLoading,
  createDiscountedTransportChargeSuccess,
  getCreatedTransportCharge,
  getTransportAreas,
  getTransportAreasError,
  getTransportAreasLoading,
  getTransportDistricts,
  getTransportDistrictsError,
  getTransportDistrictsLoading,
  getTransportLoads,
  getTransportLoadsError,
  getTransportLoadsLoading,
  getTransportOptions,
  getTransportOptionsError,
  getTransportOptionsLoading,
  getTransportPriceList,
  getTransportPriceListError,
  getTransportPriceListLoading,
  updateDiscountedTransportChargeError,
  updateDiscountedTransportChargeLoading,
  updateDiscountedTransportChargeSuccess,
} from "src/app/root-store/kwikspace-app-store/selectors/transport.selector";
import { RentalSize } from "src/app/models/RentalSize.model";
import { RentalPeriod } from "src/app/models/RentalPeriod.model";
import { ModalComponent } from "src/app/core/components/modal/modal.component";
import {
  GetBuildings,
  GetOrganizations,
  GetProvinces,
  GetValidities,
} from "src/app/root-store/kwikspace-app-store/actions/kwikspace.action";
import {
  getBuildings,
  getBuildingsError,
  getBuildingsLoading,
  getLocationsError,
  getLocationsLoading,
  getOrganizations,
  getOrganizationsError,
  getOrganizationsLoading,
  getProvinces,
  getProvincesError,
  getProvincesLoading,
  getValidities,
  getValiditiesError,
  getValiditiesLoading,
} from "src/app/root-store/kwikspace-app-store/selectors/kwikspace.selector";
import { Quote } from "src/app/models/Quote.model";

// Services
import { KwikspaceService } from "src/app/services/kwikspace.service";
import { StaticDataService } from "src/app/services/static-data.service";

@Component({
  selector: "kwikspace-edit-rental-quote",
  templateUrl: "./edit-rental-quote.component.html",
  styleUrls: ["./edit-rental-quote.component.scss"],
})
export class EditRentalQuoteComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();
  private cancelationSubscription = new Subscription();

  @ViewChild("optionalExtraModal", { static: false })
  optionalExtraModal: ModalComponent;

  @ViewChild("editOptionalExtraModal", { static: false })
  editOptionalExtraModal: ModalComponent;

  @ViewChild("quoteApproveModal", { static: false })
  quoteApproveModal: ModalComponent;

  @ViewChild("editQuoteStatusModal", { static: false })
  editQuoteStatusModal: ModalComponent;

  @ViewChild("editTransportChargeModal", { static: false })
  editTransportChargeModal: ModalComponent;

  @ViewChild("editLineItemDiscountModal", { static: false })
  editLineItemDiscountModal: ModalComponent;

  @ViewChild("noDiscountAllowedModal", { static: false })
  noDiscountAllowedModal: ModalComponent;

  @ViewChild("addClassroomModal", { static: false })
  addClassroomModal: ModalComponent;

  @ViewChild("addUnitModal", { static: false })
  addUnitModal: ModalComponent;

  @ViewChild("removeUnitModal", { static: false })
  removeUnitModal: ModalComponent;

  @ViewChild("removeOptionalExtraModal", { static: false })
  removeOptionalExtraModal: ModalComponent;

  updateQuoteLoading$: Observable<any> = this.store.select(updateQuoteLoading);
  updateQuoteError$: Observable<any> = this.store.select(updateQuoteError);

  customersLoading$: Observable<any> = this.store.select(
    getCustomerListLoading
  );
  customersError$: Observable<any> = this.store.select(getCustomerListError);

  productsLoading$: Observable<any> = this.store.select(getProductListLoading);
  productsError$: Observable<any> = this.store.select(getProductListError);

  rentalPricingLoading$: Observable<any> = this.store.select(
    getRentalPriceListLoading
  );
  rentalPricingError$: Observable<any> = this.store.select(
    getRentalPriceListError
  );

  rentalSizesLoading$: Observable<any> = this.store.select(
    getRentalSizesLoading
  );
  rentalSizesError$: Observable<any> = this.store.select(getRentalSizesError);

  rentalPeriodLoading$: Observable<any> = this.store.select(
    getRentalPeriodsLoading
  );
  rentalPeriodError$: Observable<any> = this.store.select(
    getRentalPeriodsError
  );

  transportPricingLoading$: Observable<any> = this.store.select(
    getTransportPriceListLoading
  );
  transportPricingError$: Observable<any> = this.store.select(
    getTransportPriceListError
  );

  transportOptionsLoading$: Observable<any> = this.store.select(
    getTransportOptionsLoading
  );
  transportOptionsError$: Observable<any> = this.store.select(
    getTransportOptionsError
  );

  transportLoadsLoading$: Observable<any> = this.store.select(
    getTransportLoadsLoading
  );
  transportLoadsError$: Observable<any> = this.store.select(
    getTransportLoadsError
  );

  transportAreasLoading$: Observable<any> = this.store.select(
    getTransportAreasLoading
  );
  transportAreasError$: Observable<any> = this.store.select(
    getTransportAreasError
  );

  transportDistrictsLoading$: Observable<any> = this.store.select(
    getTransportDistrictsLoading
  );
  transportDistrictsError$: Observable<any> = this.store.select(
    getTransportDistrictsError
  );

  createProductConfigurationLoading$: Observable<any> = this.store.select(
    getCreatedProductConfigurationLoading
  );
  createProductConfigurationError$: Observable<any> = this.store.select(
    getCreatedProductConfigurationError
  );

  updateProductConfigurationLoading$: Observable<any> = this.store.select(
    updateProductConfigurationLoading
  );
  updateProductConfigurationError$: Observable<any> = this.store.select(
    updateProductConfigurationError
  );

  createLineItemLoading$: Observable<any> = this.store.select(
    getCreatedLineItemsLoading
  );
  createLineItemError$: Observable<any> = this.store.select(
    getCreatedLineItemsError
  );

  updateLineItemLoading$: Observable<any> = this.store.select(
    updateRentalLineItemLoading
  );
  updateLineItemError$: Observable<any> = this.store.select(
    updateRentalLineItemError
  );

  provincesLoading$: Observable<any> = this.store.select(getProvincesLoading);
  provincesError$: Observable<any> = this.store.select(getProvincesError);

  buildingsLoading$: Observable<any> = this.store.select(getBuildingsLoading);
  buildingsError$: Observable<any> = this.store.select(getBuildingsError);

  organizationsLoading$: Observable<any> = this.store.select(
    getOrganizationsLoading
  );
  organizationsError$: Observable<any> = this.store.select(
    getOrganizationsError
  );

  locationsLoading$: Observable<any> = this.store.select(getLocationsLoading);
  locationsError$: Observable<any> = this.store.select(getLocationsError);

  validitiesLoading$: Observable<any> = this.store.select(getValiditiesLoading);
  validitiesError$: Observable<any> = this.store.select(getValiditiesError);

  quoteLoading$: Observable<any> = this.store.select(getQuoteLoading);
  quoteError$: Observable<any> = this.store.select(getQuoteError);

  createTransportChargeLoading$: Observable<any> = this.store.select(
    createDiscountedTransportChargeLoading
  );
  createTransportChargeError$: Observable<any> = this.store.select(
    createDiscountedTransportChargeError
  );

  updateTransportChargeLoading$: Observable<any> = this.store.select(
    updateDiscountedTransportChargeLoading
  );
  updateTransportChargeError$: Observable<any> = this.store.select(
    updateDiscountedTransportChargeError
  );

  rentalLoading: boolean = false;

  customers: Customer[] = [];
  products: Product[] = [];
  filteredProducts: Product[] = [];
  rentalPricing: Rental[] = [];
  rentalOptions: Rental[] = [];
  rentalSizes: RentalSize[] = [];
  rentalPeriods: RentalPeriod[] = [];
  transportPricing: Transport[] = [];
  transportOptions: any[] = [];
  transportLoads: any[] = [];
  transportAreas: any[] = [];
  transportDistricts: any[] = [];


  selectedTransportLoad = null;
  selectedTransportLoadOne = null;
  selectedTransportLoadTwo = null;
  selectedTransportArea = null;
  selectedTransportOption = null;
  selectedTransportJoinOption = null;
  selectedLocation = null;
  addSecondTransportLoad: boolean = false;
  loadOneQtyUpdate = new Subject<number>();
  loadTwoQtyUpdate = new Subject<number>();
  editingTransportLoad = null;
  transportType = "load1";
  transportOneLoading: boolean = false;
  transportOneHasPricing: boolean = false;
  transportTwoLoading: boolean = false;
  transportTwoHasPricing: boolean = false;

  provinces: any[] = [];
  buildingTypes: any[] = [];
  organizationalTypes: any[] = [];
  quoteLocations: any[] = [];
  quoteValidities: any[] = [];

  selectedProvince;
  selectedDistrict;
  selectedArea;
  selectedBuildingType;
  selectedOrganizationalSector;

  selectedSize: string = null;
  selectedPeriod: string = null;
  selectedOption: any = null;
  selectedOptionId: any = null;
  selectedProduct: any = null;
  selectedProductId: any = null;
  selectedTransport: any = null;
  showOptionDescription: boolean = false;
  showTransport: boolean = false;
  showRentalOptions: boolean = false;
  hasPricing: boolean = false;
  hasTransportPricing: boolean = false;
  showTransportSize: boolean = false;
  isSaProvince: boolean = false;
  canApprove: boolean = false;
  canUpdateStatus: boolean = false;
  rentalMonths: any = null;

  showProvinceMsg: boolean = false;
  showDistricts: boolean = false;
  showAreas: boolean = false;
  isClassroom: boolean = false;

  addressForm: FormGroup;
  quoteForm: FormGroup;
  rentalForm: FormGroup;
  extrasForm: FormGroup;
  editExtrasForm: FormGroup;
  editQuoteForm: FormGroup;
  editChargeForm: FormGroup;
  editLineItemDiscountForm: FormGroup;

  extrasFormIsDisabled: boolean = true;
  editExtrasFormIsDisabled: boolean = true;

  extrasFormData: Object = {
    product: null,
  };
  editExtrasFormData: Object = {
    product: null,
  };

  user: any = null;
  isAdmin: boolean = false;
  isStaff: boolean = false;
  isCustomer: boolean = false;

  changeDeliveryArea: boolean = false;
  selectedDeliveryArea: string = "";

  searchText: string = "";
  searchTextProduct: string = "";

  quoteLineItem: any = {};
  selectedModalLineItem = null;
  selectedModalLineItemExtra = null;
  editingExtra: boolean = false;
  deletingExtra: boolean = false;
  noProductsAvailable: boolean = false;

  showTransportCharge: boolean = false;

  preloadingEditForm: boolean = false;
  isEditing: boolean = false;
  preloadSecondTransport: boolean = false;
  savingUnit: boolean = false;

  actualMonths: number = 1;
  unitQty: number = 1;
  rentalMonthsErrorMsg: string = "Actual months is required";
  rentalMonthsError: boolean = true;
  qtyEntered: boolean = false;
  showQty: boolean = true;

  quote: Quote = null;
  quoteId: any = null;
  customerId: any = null;

  selectedUnit: any = null;

  quoteStatus: any[] = [];
  selectedQuoteStatus: any = null;

  priceDiscountChecked: boolean = true;
  manualChargeChecked: boolean = false;
  canAddDiscount: boolean = false;

  itemForChargeChange: any = null;
  unitForDiscount: any = null;

  discountError: boolean = false;
  limitationError: boolean = false;
  limitation: any = "";
  amountToChange: any = null;

  newUnit: boolean = false;
  editUnit: boolean = false;
  additionalUnit: boolean = false;

  unitTitle: string = "";

  activeStep: number = 1;
  stepper = [
    {
      id: 0,
      name: "Edit",
      status: "progress",
    },
    {
      id: 1,
      name: "Customer Details",
      status: "todo",
    },
    {
      id: 2,
      name: "Quote",
      status: "todo",
    },
  ];

  isMobile: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    window.innerWidth <= 768
  );

  @HostListener("window:resize", ["$event"])
  setIsMobile(event) {
    const isMobile = event.target.innerWidth <= 768;
    this.isMobile.next(isMobile);
  }

  constructor(
    private store: Store<RootStoreState.RootState>,
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private toastrService: TanToastrService,
    private kwikspaceService: KwikspaceService,
    private staticDataService: StaticDataService
  ) { }

  ngOnInit() {
    const { params } = this.route.snapshot;
    this.quoteId = params.id;

    this.quoteStatus = this.staticDataService.getQuoteStatus();

    this.subscribeToSubjects();
    this.initializeState();
    this.registerSelects();
    this.initializeForms();

    // Validity checkers for modals
    this.extrasForm.statusChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((valid) => {
        this.extrasFormIsDisabled = valid === "VALID" ? false : true;
      });
    this.editExtrasForm.statusChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((valid) => {
        this.editExtrasFormIsDisabled = valid === "VALID" ? false : true;
      });

    // Keep current form state, need this for updating line items
    this.extrasForm.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data) => {
        this.extrasFormData = data;
      });
    this.editExtrasForm.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data) => {
        this.editExtrasFormData = data;
      });
  }

  subscribeToSubjects() {
    this.loadOneQtyUpdate.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.setSelectedTransportLoadOne()
      });

    this.loadTwoQtyUpdate.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.setSelectedTransportLoadTwo()
      });
  }

  registerSelects() {
    this.store
      .select(getProductList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((products: any) => {
        if (products && products.length > 0) {
          let matchedProducts = [];

          this.products = products;

          if (!this.editingExtra && this.selectedModalLineItem) {
            const unitSize = this.selectedModalLineItem.rentalOption.size.size;
            const unitType = this.selectedModalLineItem.rentalOption.item.description;

            this.products.forEach((product: any) => {
              if (
                product.size === unitSize &&
                product.office.toLowerCase() === unitType.toLowerCase()
              ) {
                matchedProducts.push(product);
              } else if (product.size == "0" && product.office == "0") {
                matchedProducts.push(product);
              }
            });

            if (matchedProducts.length > 0) {
              // Remove what's already added on the unit
              this.filteredProducts = matchedProducts.filter(
                (extra) => !this.selectedModalLineItem.productConfigurations.find(
                  (config) => config.product.id === extra.id
                )
              ) 
            } else {
              this.noProductsAvailable = true;
              this.filteredProducts = [];
            }
          } else {
            this.filteredProducts = this.products;
          }

          if (this.editingExtra && this.selectedModalLineItemExtra) {
            this.extrasForm.get("quantity").setValue(this.selectedModalLineItemExtra.quantity / this.selectedModalLineItem.quantity);
            this.extrasForm.get("product").setValue(this.selectedModalLineItemExtra.product.id);
          }
        }
      });

    this.store
      .select(getCreatedProductConfigurationSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((products) => {
        if (products) {
          this.toastrService.showSuccessToaster(
            "Success",
            "Quote updated",
            null
          );

          this.optionalExtraModal.close();
          this.selectedProductId = null;
          this.selectedProduct = null;

          this.initializeState();
          this.resetForms();
        }
      });

    this.store
      .select(updateProductConfigurationSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((lineItem) => {
        if (lineItem) {
          if (this.editingExtra) {
            this.toastrService.showSuccessToaster(
              "Success",
              "Optional extra updated",
              null
            );
            this.editOptionalExtraModal.close();
          } else {
            this.toastrService.showSuccessToaster(
              "Success",
              "Optional extra removed",
              null
            );
            this.removeOptionalExtraModal.close();
          }

          this.store.dispatch(new ClearUpdateProductConfiguration());

          this.initializeState();
          this.resetForms();
        }
      });

    this.store
      .select(getProvinces)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((provinces: any) => {
        if (provinces && provinces.results) {
          this.provinces = provinces.results;
        }
      });

    this.store
      .select(updateRentalLineItemSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((lineItem) => {
        if (lineItem) {
          if (this.isEditing) {
            this.toastrService.showSuccessToaster(
              "Success",
              "Unit updated",
              null
            );
            this.addUnitModal.close();
          } else {
            this.toastrService.showSuccessToaster(
              "Success",
              "Unit removed",
              null
            );
            this.removeUnitModal.close();
          }
          
          this.initializeState();
          this.resetForms();
        }
      });

    this.store
      .select(getCreatedLineItemsSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((item) => {
        if (item) {
          this.toastrService.showSuccessToaster(
            "Success",
            "Unit created",
            null
          );
          this.addUnitModal.close();

          this.resetForms();
          this.initializeState();
        }
      });

    this.store
      .select(getBuildings)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((buildings: any) => {
        if (buildings) {
          this.buildingTypes = buildings;
        }
      });

    this.store
      .select(getOrganizations)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((organizations: any) => {
        if (organizations) {
          this.organizationalTypes = organizations;
        }
      });

    this.store
      .select(getValidities)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((validities: any) => {
        if (validities) {
          this.quoteValidities = validities;
        }
      });

    this.store
      .select(getRentalSizes)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((sizes) => {
        if (sizes) {
          this.rentalSizes = sizes;
        }
      });

    this.store
      .select(getRentalPeriods)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((periods) => {
        if (periods) {
          this.rentalPeriods = periods;
        }
      });

    // this.store
    //   .select(updateRentalLineItemSuccess)
    //   .pipe(takeUntil(this.ngUnsubscribe))
    //   .subscribe((lineItem) => {
    //     if (lineItem) {

    //       this.initializeState();
    //     }
    //   });

    this.store
      .select(getQuote)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((quote) => {
        if (quote) {
          this.quote = quote;
          this.isSaProvince =
            this.quote.shippingProvince.name != "Other" ? true : false;
          this.canApprove = this.quote.quoteStatus == "new" ? true : false;
          this.isClassroom = this.quote.building.name == "Schools & Classrooms" ? true : false;
          this.loadData();
          this.checkStatus();
        }
      });

    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          this.user = res;
          this.isStaff = res.isStaff;
          this.isCustomer = !res.isStaff && res.customer != null ? true : false;
          this.isAdmin = res.isSuperuser;

          if (this.isStaff && !this.isCustomer) {
            this.activeStep = 0;
            this.getCustomers();
          } else {
            this.activeStep = 2;
            this.loadData();
          }

          this.checkStatus();
        }
      });

    this.store
      .select(getTransportOptions)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((options: any) => {
        if (options && options.length > 0) {
          this.transportOptions = options;
          let transportId: any = null;
          let transportJoinId: any = null;

          this.transportOptions.forEach((option) => {
            if (option.name == "Transport") {
              transportId = option.id;
            } else {
              transportId = null;
            }
          });

          if (transportId == null) {
            this.transportOptions.forEach((option) => {
              if (option.name == "Delivery and Collection") {
                transportId = option.id;
              }
            });
          }

          this.selectedTransportOption = transportId;

          if (this.selectedTransportOption) {
            this.setSelectedTransportOption();
          }

          if (this.isClassroom) {
            this.transportOptions.forEach((option) => {
              if (option.name == "Travel & Siting (Joining) on site") {
                transportJoinId = option.id;
              }
            });

            if (transportJoinId == null) {
              this.transportOptions.forEach((option) => {
                if (option.name == "Siting and Dismantling") {
                  transportJoinId = option.id;
                }
              });
            }

            this.selectedTransportJoinOption = transportJoinId;
            if (this.selectedTransportJoinOption) {
              this.setSelectedTransportOption();
            }
          }
        }
      });

    this.store
      .select(getTransportPriceList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((transports: any) => {
        if (transports == null) return;
        if (this.isClassroom) return;

        if (transports?.results?.length > 0) {
          if (
            this.transportType === "load1" &&
            this.selectedTransportLoadOne?.results === null
          ) {
            this.filterTransportList("load1", transports.results, this.editingTransportLoad);
          } else if (
            this.transportType === "load2" &&
            this.selectedTransportLoadTwo?.results === null
          ) {
            this.filterTransportList("load2", transports.results, this.editingTransportLoad);
          }
        } else if (transports?.results?.length === 0) {
          if (this.transportType === "load1") {
            this.transportOneHasPricing = false;
            this.transportOneLoading = false;
          } else if (this.transportType === "load2") {
            this.transportTwoHasPricing = false;
            this.transportTwoLoading = false;
          }

          this.preloadingEditForm = false;
        }
      });

    this.store
      .select(getTransportPriceList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((transports: any) => {
        if (!this.isClassroom) return;

        if (transports && transports.results) {
          if (transports.results.length > 0) {
            this.transportPricing = [];
            transports.results.forEach((price) => {
              if (
                price.priceType.name == "Both" ||
                price.priceType.name == "Rentals"
              ) {
                if (
                  price.transportCharge.name == "Transport" ||
                  price.transportCharge.name == "Delivery and Collection" ||
                  price.transportCharge.name ==
                  "Travel & Siting (Joining) on site" ||
                  price.transportCharge.name == "Siting and Dismantling"
                ) {
                  this.transportPricing.push(price);
                }
              }
            });
            this.hasTransportPricing =
              this.transportPricing.length > 0 ? true : false;

            this.setTransportPricing();
          } else {
            this.hasTransportPricing = false;
            this.rentalLoading = false;
            this.preloadingEditForm = false;
          }
        }
      });

    this.store
      .select(getRentalPriceList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((rentals: any) => {
        if (rentals && rentals.results) {
          if (rentals.results.length > 0) {
            this.hasPricing = true;
            this.rentalPricing = rentals.results;

            if (this.isEditing) {
              const rentalOption = this.rentalPricing.find((option) => {
                if (this.selectedUnit.rentalOption.id === option.id) return option;
              });

              if (!rentalOption) return;

              this.rentalForm.controls.rental_option.setValue(rentalOption.id);
              this.setRentalOption();
            }
          } else {
            this.hasPricing = false;
          }
        }
      });

    this.store
      .select(getTransportLoads)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((loads: any) => {
        if (loads && loads.length > 0) {
          this.transportLoads = loads.filter(
            (loadSize) => loadSize.available == true
          );
          this.transportLoads = this.transportLoads.filter(
            (loadSize) => loadSize.hasRental == true
          );

          this.store.dispatch(new GetTransportOptions());

          if (this.isClassroom) {
            const loadId = this.transportLoads.findIndex(
              (obj) => obj.size == "7m x 7m"
            );
            this.selectedTransportLoad = this.transportLoads[loadId].id;

            if (this.selectedTransportLoad) {
              this.setSelectedTransportLoad();
            }
          }
        }
      });
  }

  initializeState() {
    this.store.dispatch(new ClearQuoteList());
    this.store.dispatch(new GetQuote(this.quoteId));
    this.store.dispatch(new ClearRentalLineItems());
    this.store.dispatch(new ClearProductConfiguration());
    this.store.dispatch(new ClearRentalUpdate());
  }

  initializeForms() {
    this.quoteForm = this.formBuilder.group({
      customer: [{ value: null, disabled: true }, [Validators.required]],
      searchCustomer: [null, []],
      building_type: [null, [Validators.required]],
      organization_sector: [null, [Validators.required]],
      your_reference: [null, []],
      customer_account_number: [null, []],
      site_allocation: [null, []],
      quote_validity: [null, []],
      quote_location: [null, []],
      notes: [null, []],
      line_items: [null, []],
      purchase_line_items: [null, []],
    });

    this.addressForm = this.formBuilder.group({
      shipping_address1: [null, [Validators.required]],
      shipping_address2: [null, []],
      shipping_suburb: [null, [Validators.required]],
      shipping_province: [null, [Validators.required]],
      shipping_district: [null, []],
      shipping_area: [null, []],
      shipping_postal: [null, []],
      edit_delivery: [null, []],
    });

    this.rentalForm = this.formBuilder.group({
      rental_period: [null, [Validators.required]],
      rental_months: [null, [Validators.required]],
      rental_size: [null, [Validators.required]],
      rental_option: [null, [Validators.required]],
      unit_qty: [null, [Validators.required]],
      optional_extras: [null, []],
      rental_transport: [null, [Validators.required]],
      transport_option: [null, [Validators.required]],
      transport_area: [null, [Validators.required]],
      transport_load_one: [null, [Validators.required]],
      transport_load_one_qty: [null, [Validators.required]],
      transport_load_two: [null, []],
      transport_load_two_qty: [null, []],
      transport_config: [null, [Validators.required]],
    });

    this.extrasForm = this.formBuilder.group({
      product: [null, [Validators.required]],
      searchProduct: [null, []],
      quantity: [null, [Validators.required]],
      name: [null, []],
    });

    this.editExtrasForm = this.formBuilder.group({
      product: [null, [Validators.required]],
      searchProduct: [null, []],
      quantity: [null, [Validators.required]],
      name: [null, []],
    });

    this.editQuoteForm = this.formBuilder.group({
      quote_status: [null, []],
    });

    this.editChargeForm = this.formBuilder.group({
      discount: [null, [Validators.max(15)]],
      discount_amount: [null, []],
      transport_charge: [null, []],
      transport_location: [null, []],
      load_size: [null, []],
      price: [null, []],
    });

    this.editLineItemDiscountForm = this.formBuilder.group({
      discount: [null, [Validators.max(15)]],
      discount_amount: [null, []],
    });
  }

  resetForms() {
    this.quoteForm.reset();
    this.addressForm.reset();
    this.rentalForm.reset();
    this.extrasForm.reset();
    this.editExtrasForm.reset();
    this.editQuoteForm.reset();
    this.editChargeForm.reset();
    this.editLineItemDiscountForm.reset();

    this.quoteLineItem = {};

    this.selectedProduct = null;
    this.selectedSize = null;
    this.selectedOption = null;

    this.showQty = true;
    this.showTransportSize = false;
    this.showTransport = false;
    this.showTransportCharge = false;
    this.showRentalOptions = false;
    this.isClassroom = false;
    this.addSecondTransportLoad = false;
    this.showOptionDescription = false;
    this.qtyEntered = false;
    this.savingUnit = false;
    this.isEditing = false;
    this.preloadingEditForm = false;
    this.selectedModalLineItem = false;
    this.selectedModalLineItemExtra = null;
    this.selectedModalLineItem = null;
    this.editingExtra = false;
    this.noProductsAvailable = false;
  }

  checkStatus() {
    this.canUpdateStatus =
      !this.isCustomer && this.isStaff && !this.canApprove ? true : false;
  }

  loadData() {
    this.store.dispatch(new GetProvinces());
    this.store.dispatch(new GetBuildings());
    this.store.dispatch(new GetOrganizations());
    this.store.dispatch(new GetValidities());
    this.store.dispatch(new GetRentalPeriod());
    this.store.dispatch(new GetRentalSize());
  }

  getCustomers() {
    this.store
      .select(getCustomerList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((customers: any) => {
        if (customers && customers.results) {
          this.customers = customers.results;
        }
      });

    this.store.dispatch(new GetCustomerList({}));
  }

  filterTransportList(type, transportResults, load) {
    let filteredArray = [];
    transportResults.forEach((price) => {
      if (price.priceType.name == "Both" || price.priceType.name == "Rentals") {
        if (
          price.transportCharge.name == "Transport" ||
          price.transportCharge.name == "Delivery and Collection"
        ) {
          filteredArray.push(price);
        }
      }
    });

    if (type === "load1") {
      this.selectedTransportLoadOne.results = filteredArray[0];

      const transport = {
        id: this.selectedTransportLoadOne.results.id,
        transport: this.selectedTransportLoadOne.results,
        quantity: load.quantity,
      };

      this.quoteLineItem.transportOne = transport;
      this.transportOneLoading = false;
      this.transportOneHasPricing = true;
      this.showOptionDescription = this.selectedOption != null ? true : false;

      if (this.preloadSecondTransport) {
        this.setSelectedTransportLoadTwo();
      } else {
        this.preloadingEditForm = false;
      }
    } else if (type === "load2") {
      this.selectedTransportLoadTwo.results = filteredArray[0];

      const transport = {
        id: this.selectedTransportLoadTwo.results.id,
        transport: this.selectedTransportLoadTwo.results,
        quantity: load.quantity,
      };

      this.quoteLineItem.transportTwo = transport;
      this.transportTwoLoading = false;
      this.transportTwoHasPricing = true;
      this.showOptionDescription = this.selectedOption != null ? true : false;
      this.preloadingEditForm = false;
    }

    this.preloadSecondTransport = false;
  }

  openApproveCustomerScreen() {
    this.activeStep = 1;
    this.setCustomerQuoteDetail();
  }

  openApproveQuoteScreen() {
    this.activeStep = 2;
    this.loadData();
  }

  setCustomerQuoteDetail() {
    this.quoteForm
      .get("customer")
      .setValue(
        this.quote.customer.firstName + " " + this.quote.customer.lastName
      );
    this.quoteForm.get("building_type").setValue(this.quote.building.id);
    this.quoteForm
      .get("organization_sector")
      .setValue(this.quote.organization.id);
    this.quoteForm.get("notes").setValue(this.quote.notes);

    this.addressForm
      .get("shipping_address1")
      .setValue(this.quote.shippingAddress1);
    this.addressForm
      .get("shipping_address2")
      .setValue(this.quote.shippingAddress2);
    this.addressForm.get("shipping_suburb").setValue(this.quote.shippingSuburb);
    this.quote.shippingPostal
      ? this.addressForm
        .get("shipping_postal")
        .setValue(this.quote.shippingPostal)
      : this.addressForm.get("shipping_postal").setValue("");

    this.selectedDeliveryArea =
      this.quote.shippingArea.district.province.name +
      " - " +
      this.quote.shippingArea.district.name +
      " - " +
      this.quote.shippingArea.name;
  }

  editDeliveryArea(checked) {
    this.changeDeliveryArea = checked ? true : false;

    if (!this.changeDeliveryArea) {
      // reset delivery area to be same as quote
    }
  }

  setProduct() {
    this.selectedProductId = this.extrasForm.value.product;

    let _id = this.products.findIndex(
      (obj) => obj.id == this.selectedProductId
    );

    this.selectedProduct = this.products[_id];

    if (this.extrasForm.value.quantity > 0) {
      this.validateLimitation(this.extrasForm.value.quantity);
    }
  }

  setProvince() {
    this.store.dispatch(new ClearTransportDistricts());
    this.store.dispatch(new ClearTransportAreas());

    this.selectedProvince = this.addressForm.value.shipping_province;

    const _id = this.provinces.findIndex(
      (obj) => obj.id == this.selectedProvince
    );
    const province = this.provinces[_id].name;

    if (province != "Other") {
      this.showProvinceMsg = false;
      this.isSaProvince = true;
      this.showDistricts = true;
      this.getTransportDistricts();
    } else {
      this.showProvinceMsg = true;
      this.showDistricts = false;
      this.showAreas = false;
    }
  }

  setBuildingType() {
    this.selectedBuildingType = this.quoteForm.value.building_type;
  }

  setOrganizationalSector() {
    this.selectedOrganizationalSector =
      this.quoteForm.value.organization_sector;
  }

  updateCustomerQuote() {
    const payload: any = {
      quote_status: this.quote.quoteStatus,
      quote_owner: this.quote.quoteOwner.id,
      customer:
        this.customerId && this.customerId != null
          ? this.customerId
          : this.quote.customer.id,
      quote_validity: this.quote.quoteValidity.id,
      building: this.selectedBuildingType
        ? this.selectedBuildingType
        : this.quote.building.id,
      organization: this.selectedOrganizationalSector
        ? this.selectedOrganizationalSector
        : this.quote.organization.id,
      notes: this.quoteForm.value.notes
        ? this.quoteForm.value.notes
        : this.quote.notes,
      quote_location: this.quote.quoteLocation.id,
      your_reference: this.kwikspaceService.getNewQuoteRevisionString(
        this.quote.yourReference
      ),
      shipping_address1: this.addressForm.value.shipping_address1,
      shipping_address2: this.addressForm.value.shipping_address2,
      shipping_postal: this.addressForm.value.shipping_postal,
      shipping_province: this.selectedProvince
        ? this.selectedProvince
        : this.quote.shippingProvince.id,
      shipping_area: this.selectedArea
        ? this.selectedArea
        : this.quote.shippingArea.id,
      shipping_suburb: this.addressForm.value.shipping_suburb,
    };

    this.store
      .select(updateQuoteSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((quote) => {
        if (quote) {
          this.toastrService.showSuccessToaster(
            "Success",
            "Quote updated",
            null
          );
          this.activeStep = 2;
          this.loadData();
        }
      });

    this.store.dispatch(new UpdateQuote(payload, this.quote.id));
    this.store.dispatch(new EmailQuote(this.quote.id));
  }

  setQuoteStatus() {
    this.selectedQuoteStatus = this.editQuoteForm.value.quote_status;
  }

  updateQuoteStatus() {
    this.editQuoteStatusModal.open();
  }

  updateQuote() {
    const payload: any = {
      quote_status:
        this.quote.quoteStatus == "new" ? "approved" : this.quote.quoteStatus,
    };

    this.store
      .select(updateQuoteSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((quote) => {
        if (quote) {
          this.toastrService.showSuccessToaster(
            "Success",
            "Quote updated",
            null
          );
          if (this.activeStep == 2) {
            const route = AppRoutes.PWA + AppRoutes.QUOTE_LIST;
            this.router.navigate([route]);
          }
        }
      });

    this.store.dispatch(new UpdateQuote(payload, this.quote.id));
    this.store.dispatch(new EmailQuote(this.quote.id));
  }

  saveUpdateQuote() {
    if (this.selectedQuoteStatus) {
      const payload: any = {
        quote_status: this.selectedQuoteStatus,
      };

      this.store
        .select(updateQuoteSuccess)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((quote) => {
          if (quote) {
            this.toastrService.showSuccessToaster(
              "Success",
              "Quote updated",
              null
            );
            this.editQuoteStatusModal.close();

            const route = AppRoutes.PWA + AppRoutes.QUOTE_LIST;
            this.router.navigate([route]);
          }
        });

      this.store.dispatch(new UpdateQuote(payload, this.quote.id));
      this.store.dispatch(new EmailQuote(this.quote.id));
    }
  }

  updateQuoteReference() {
    const updatedRef = this.kwikspaceService.getNewQuoteRevisionString(
      this.quote.yourReference
    );
    const payload: any = {
      your_reference: updatedRef,
    };

    this.store
      .select(updateQuoteSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((quote) => {
        if (quote) {
          this.toastrService.showSuccessToaster(
            "Success",
            "Quote revision updated",
            null
          );
          this.initializeState();
        }
      });

    this.store.dispatch(new UpdateQuote(payload, this.quote.id));
    this.store.dispatch(new EmailQuote(this.quote.id));
  }

  addUnit() {
    this.rentalForm.reset();
    this.unitTitle = "Add Unit";
    this.newUnit = true;
    this.additionalUnit =
      this.quote.lineItems && this.quote.lineItems.length > 0 ? true : false;

    if (this.additionalUnit) {
      this.selectedPeriod = this.quote.lineItems[0].rentalOption.period.id;
      this.rentalForm.controls.rental_months.setValue(this.quote.lineItems[0].rentalPeriod);
      this.rentalMonthsError = false;
    }
    this.addUnitModal.open();
  }

  editLineItem(item) {
    this.isEditing = true;
    this.preloadingEditForm = true;
    this.isClassroom = item.name.includes("Classroom");
    this.rentalForm.reset();
    this.unitTitle = "Edit Unit";
    this.newUnit = false;
    this.additionalUnit =
      this.quote.lineItems && this.quote.lineItems.length === 0 ? true : false;
    this.selectedUnit = item;
    this.showQty = this.selectedUnit.productConfigurations.length === 0;
    this.addUnitModal.open();
    this.setEditFormWithLineItem(item);
  }

  setEditFormWithLineItem(item) {
    this.rentalForm.controls.rental_period.setValue(
      item.rentalOption.period.id
    );
    this.rentalForm.controls.rental_months.setValue(item.rentalPeriod);
    this.rentalForm.controls.rental_size.setValue(item.rentalOption.size.id);
    this.rentalForm.controls.unit_qty.setValue(item.quantity);
    this.rentalForm.markAllAsTouched();

    this.selectedPeriod = item.rentalOption.period.id;
    this.selectedSize = item.rentalOption.size.id;

    this.selectedOption = null;
    this.showOptionDescription = false;

    this.getRentalOptions();
    this.validateMinMaxMonths();
    this.setUnitQty();

    if (!this.isClassroom) {
      this.setTruckOptions();
    }
  }

  setTruckOptions() {
    this.rentalForm.controls.transport_load_one.setValue(this.selectedUnit.deliveryCharge.loadSize.id);
    this.rentalForm.controls.transport_load_one_qty.setValue(this.selectedUnit.deliveryChargeQty);

    if (this.selectedUnit.deliveryChargeExtra) {
      this.preloadSecondTransport = true;
      this.addSecondTransportLoad = true;
      this.rentalForm.controls.transport_load_two.setValue(this.selectedUnit.deliveryChargeExtra.loadSize.id);
      this.rentalForm.controls.transport_load_two_qty.setValue(this.selectedUnit.deliveryChargeExtraQty);
    }

    this.setSelectedTransportLoadOne();
  }

  addExtraToLineItem(lineItem) {
    this.selectedModalLineItem = lineItem;
    this.optionalExtraModal.open();
    this.getProducts();
  }

  setTransportPricing() {
    if (this.transportPricing.length > 0) {
      this.setTransportOption();
    } else {
      this.showTransportCharge = true;
    }
  }

  setTransportOption() {
    let _id;
    let _option;
    let _transportOption = null;
    let _transportOptionJoin = null;

    for (let i = 0; i < this.transportPricing.length; i++) {
      _option = this.transportPricing[i];

      if (_option.transportCharge.name == "Transport") {
        _transportOption = _option;
        this.selectedTransport = _option;
      } else if (
        _transportOption == null &&
        _option.transportCharge.name == "Delivery and Collection"
      ) {
        _transportOption = _option;
        this.selectedTransport = _option;
      }

      if (_option.transportCharge.name == "Travel & Siting (Joining) on site") {
        _transportOptionJoin = _option;
        this.selectedTransportJoinOption = _option;
      } else if (
        _transportOptionJoin == null &&
        _option.transportCharge.name == "Siting and Dismantling"
      ) {
        _transportOptionJoin = _option;
        this.selectedTransportJoinOption = _option;
      }
    }

    if (this.selectedTransport == null) {
      const transportId = this.rentalForm.value.rental_transport;
      _id = this.transportPricing.findIndex((obj) => obj.id == transportId);
      this.selectedTransport = this.transportPricing[_id];
    }

    const transport = {
      id: this.selectedTransport.id,
      transport: this.selectedTransport,
    };

    this.quoteLineItem.transportOne = transport;

    const requireJoin = this.requireJoining();
    if (requireJoin) {
      if (this.selectedTransportJoinOption != null) {
        const transportJoin = {
          id: this.selectedTransportJoinOption.id,
          transport: this.selectedTransportJoinOption,
        };

        this.quoteLineItem.transportJoin = transportJoin;
      }
    }

    this.showOptionDescription = this.selectedOption != null ? true : false;

    this.rentalLoading = false;
    this.preloadingEditForm = false;
  }

  setPeriod() {
    this.selectedPeriod = this.rentalForm.value.rental_period;
    this.getRentalOptions();
    this.validateMinMaxMonths();
  }

  setSize() {
    this.selectedSize = this.rentalForm.value.rental_size;
    this.getRentalOptions();
  }

  setRentalOption() {
    this.rentalLoading = true;

    this.selectedOptionId = this.rentalForm.value.rental_option;
    const _id = this.rentalPricing.findIndex(
      (obj) => obj.id == this.selectedOptionId
    );
    this.selectedOption = this.rentalPricing[_id];
    this.isClassroom = this.selectedOption.category.name.includes("Classroom");

    const lineItem = {
      name: "Unit " + (this.quote.lineItems.length + 1),
      item: this.selectedOption,
      rental_period: this.rentalForm.value.rental_months,
      quantity: this.rentalForm.value.unit_qty
        ? this.rentalForm.value.unit_qty
        : 1,
      transportOne: "",
      transportTwo: "",
      transportJoin: "",
      extras: [],
      selected_period_id: this.selectedPeriod,
    };

    this.quoteLineItem = lineItem;

    if (this.isSaProvince) {
      this.showTransport = true;

      if (this.isClassroom) {
        this.store.dispatch(new GetTransportLoads());
      } else {
        this.showTransportSize = true;
        this.rentalLoading = false;
      }
    } else {
      this.showOptionDescription = this.selectedOption != null ? true : false;
    }
  }

  getRentalOptions() {
    if (this.selectedPeriod != null && this.selectedSize != null) {
      this.getRentalPricing();
      this.store.dispatch(new GetTransportLoads());
      this.showRentalOptions = true;
    } else {
      this.showRentalOptions = false;
    }
  }

  getRentalPricing() {
    this.store.dispatch(new ClearRentalList());
    let params = `?period=${this.selectedPeriod}&size=${this.selectedSize}`;
    this.store.dispatch(new GetRentalPrice(params));
  }

  getRentalPriceList() {
    this.store.dispatch(new ClearRentalList());

    this.store
      .select(getRentalPriceList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((rentals: any) => {
        if (rentals && rentals.results) {
          if (rentals.results.length > 0) {
            this.hasPricing = true;
            this.rentalPricing = rentals.results;
          }
        }
      });

    this.store.dispatch(new GetRentalList({}));
  }

  requireJoining() {
    let join = false;

    const _id = this.rentalSizes.findIndex(
      (obj) => obj.id == this.selectedSize
    );
    const size = _id >= 0 && this.rentalSizes[_id].size ? this.rentalSizes[_id].size : "0";

    let min = size.split("x")[0].replace(/\s/g, "").charAt(0);
    min = parseInt(min);

    join = min > 3 ? true : false;

    return join;
  }

  getTransportAllPricing() {
    this.store.dispatch(new ClearTransportPriceList());

    this.store
      .select(getTransportPriceList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((transports: any) => {
        if (transports && transports.results) {
          this.transportPricing = transports.results;
          this.hasTransportPricing =
            this.transportPricing.length > 0 ? true : false;
        }
      });

    this.store.dispatch(new GetTransportPriceList({}));
  }

  getTransportDistricts() {
    const payload = {
      filter: {
        filterCategory: "province",
        filter: this.selectedProvince,
      },
    };

    this.store.dispatch(new GetTransportDistricts(payload));

    this.store
      .select(getTransportDistricts)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((districts: any) => {
        if (districts) {
          this.transportDistricts = districts;
        }
      });
  }

  setDistrict() {
    this.selectedDistrict = this.addressForm.value.shipping_district;
    this.showAreas = true;
    this.getTransportAreas();
  }

  getTransportAreas() {
    const payload = {
      filter: {
        filterCategory: "district",
        filter: this.selectedDistrict,
      },
    };

    this.store.dispatch(new GetTransportAreas(payload));

    this.store
      .select(getTransportAreas)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((areas: any) => {
        if (areas) {
          this.transportAreas = areas;
        }
      });
  }

  setArea() {
    this.selectedArea = this.addressForm.value.shipping_area;
  }

  setSelectedTransportLoad() {
    this.selectedTransportLoad = this.selectedTransportLoad
      ? this.selectedTransportLoad
      : this.rentalForm.value.transport_load;

    this.getTransportPriceOptions();
  }

  setSelectedTransportLoadOne() {
    this.selectedTransportLoadOne = {
      load: this.rentalForm.value.transport_load_one,
      quantity: this.rentalForm.value.transport_load_one_qty,
      results: null,
    };
    if (
      this.selectedTransportLoadOne.load &&
      this.selectedTransportLoadOne.quantity
    ) {
      this.transportOneLoading = true;
      this.transportType = "load1";
      this.editingTransportLoad = this.selectedTransportLoadOne;
      this.calculateTransportPricing();
    } else {
      this.preloadingEditForm = false;
    }
  }

  setSelectedTransportLoadTwo() {
    this.selectedTransportLoadTwo = {
      load: this.rentalForm.value.transport_load_two,
      quantity: this.rentalForm.value.transport_load_two_qty,
      results: null,
    };
    if (
      this.selectedTransportLoadTwo.load &&
      this.selectedTransportLoadTwo.quantity
    ) {
      this.transportTwoLoading = true;
      this.transportType = "load2";
      this.editingTransportLoad = this.selectedTransportLoadTwo;
      this.calculateTransportPricing();
    } else {
      this.preloadingEditForm = false;
    }
  }

  calculateTransportPricing() {
    const shipping_area = this.quote.shippingArea ? this.quote.shippingArea.id : '';
    const params = `?load_size=${this.editingTransportLoad.load}&transport_location=${shipping_area}`;
    this.store.dispatch(new GetTransportPriceList(params));
  }

  // setSelectedTransportArea() {
  //   this.selectedTransportArea = this.selectedTransportArea ? this.selectedTransportArea : null;

  //   this.getTransportPriceOptions();
  // }

  setSelectedTransportOption() {
    this.selectedTransportOption = this.selectedTransportOption
      ? this.selectedTransportOption
      : this.rentalForm.value.transport_option;

    this.getTransportPriceOptions();
  }

  getTransportPriceOptions() {
    if (this.selectedTransportLoad && this.selectedTransportOption) {
      this.store.dispatch(new ClearTransportPriceList());

      const area = this.quote.shippingArea
        ? `&transport_location=${this.quote.shippingArea.id}`
        : "";
      const params = `?load_size=${this.selectedTransportLoad}${area}`;
      this.store.dispatch(new GetTransportPriceList(params));
    }
  }

  searchCustomers() {
    if (this.searchText !== "") {
      this.store.dispatch(new ClearCustomerList());

      this.store.dispatch(
        new GetCustomerList({
          search: this.searchText,
        })
      );
    }
  }

  getProducts() {
    this.store.dispatch(new ClearProductList());
    this.store.dispatch(new GetProductList({}));
  }

  setSelectedCustomer(customer) {
    this.customerId = customer.id;
    this.quoteForm
      .get("customer")
      .setValue(`${customer.firstName} ${customer.lastName}`);
  }

  setSelectedProduct(product) {
    if (product != null) {
      this.selectedProduct = product;
      this.selectedProductId = product.id;
    }
  }

  openEditExtraModal(lineItem, extra) {
    this.editingExtra = true;
    this.selectedModalLineItem = lineItem;
    this.selectedModalLineItemExtra = extra;
    this.setSelectedProduct(extra.product);
    this.editOptionalExtraModal.open();

    this.getProducts();
  }

  openRemoveExtraModal(lineItem, extra) {
    this.selectedModalLineItem = lineItem;
    this.selectedModalLineItemExtra = extra;
    this.removeOptionalExtraModal.open();
  }

  openRemoveUnitModal(lineItem) {
    this.selectedModalLineItemExtra = null;
    this.selectedModalLineItem = lineItem;
    this.removeUnitModal.open();
  }

  removeUnit() {
    const payload = {
      quote: null,
    };

    this.store.dispatch(
      new UpdateRentalLineItem(payload, this.selectedModalLineItem.id)
    );
  }

  editOptionalExtra() {
    if (!this.limitationError) {
      const formData = this.extrasFormData as any;

      const qty =
        this.extrasForm.value.quantity != "" &&
          this.extrasForm.value.quantity != null &&
          this.extrasForm.value.quantity > 0
          ? this.extrasForm.value.quantity * this.selectedModalLineItem.quantity
          : 1 * this.selectedModalLineItem.quantity;

      const payload = {
        name: formData.product,
        product: this.selectedProductId,
        quantity: qty,
        line_item: this.selectedModalLineItem.id,
      };

      this.store.dispatch(
        new UpdateProductConfiguration(
          payload,
          this.selectedModalLineItemExtra.id
        )
      );

      this.limitation = "";
      this.limitationError = false;
    }
  }

  removeOptionalExtra() {
    const payload = {
      name: this.selectedModalLineItemExtra.name,
      product: this.selectedModalLineItemExtra.product.id,
      line_item: null,
    };

    this.store.dispatch(
      new UpdateProductConfiguration(
        payload,
        this.selectedModalLineItemExtra.id
      )
    );
  }

  addOptionalExtraToLineItem() {
    if (!this.limitationError) {
      let name =
        this.quote.quoteNumber +
        "," +
        this.selectedModalLineItem.name +
        "," +
        (this.selectedModalLineItem.productConfigurations.length + 1);
        
      const qty =
        this.extrasForm.value.quantity != "" &&
          this.extrasForm.value.quantity != null &&
          this.extrasForm.value.quantity > 0
          ? this.extrasForm.value.quantity * this.selectedModalLineItem.quantity
          : 1 * this.selectedModalLineItem.quantity;

      const extra = {
        name,
        idx: this.selectedProductId,
        product: this.selectedProduct.name,
        quantity: qty,
      };

      this.saveProduct(extra);

      this.limitation = "";
      this.limitationError = false;
    }
  }

  saveProduct(addon) {
    const payload = {
      name: addon.name,
      product: addon.idx,
      quantity: addon.quantity,
      line_item: this.selectedModalLineItem.id,
    };

    this.updateQuoteReference();
    this.store.dispatch(new CreateProductConfiguration(payload));
  }

  saveUnit() {
    this.savingUnit = true;
    
    if (this.newUnit) {
      this.saveNewUnitConfig();
    } else {
      let payload = {};
      const _unit = this.selectedUnit;

      payload = {
        name: _unit.name,
        rental_option: this.selectedOptionId,
        rental_period: this.rentalForm.value.rental_months,
        product_configurations: [],
        delivery_charge: this.quoteLineItem.transportOne.id,
        delivery_charge_qty: this.quoteLineItem.transportOne ? this.quoteLineItem.transportOne.quantity : null,
        delivery_charge_extra: this.quoteLineItem.transportTwo ? this.quoteLineItem.transportTwo.id : null,
        delivery_charge_extra_qty: this.quoteLineItem.transportTwo ? this.quoteLineItem.transportTwo.quantity : null,
        delivery_charge_optional: this.quoteLineItem.transportJoin
          ? this.quoteLineItem.transportJoin.id
          : null,
        quantity: this.rentalForm.value.unit_qty,
        quote: this.quote.id,
      };

      this.store.dispatch(
        new UpdateRentalLineItem(payload, this.selectedUnit.id)
      );
    }
    this.updateQuoteReference();
  }

  saveNewUnitConfig() {
    let payload = {
      name: this.quoteLineItem.name,
      rental_option: this.selectedOptionId,
      rental_period: this.rentalForm.value.rental_months,
      product_configurations: [],
      delivery_charge: this.quoteLineItem.transportOne.id,
      delivery_charge_qty: this.quoteLineItem.transportOne ? this.quoteLineItem.transportOne.quantity : null,
      delivery_charge_extra: this.quoteLineItem.transportTwo ? this.quoteLineItem.transportTwo.id : null,
      delivery_charge_extra_qty: this.quoteLineItem.transportTwo ? this.quoteLineItem.transportTwo.quantity : null,
      delivery_charge_optional: this.quoteLineItem.transportJoin
        ? this.quoteLineItem.transportJoin.id
        : null,
      quantity: this.rentalForm.value.unit_qty,
      quote: this.quote.id,
    };

    this.store.dispatch(new CreateRentalLineItem(payload));
  }

  editTransportCharge(item) {
    this.itemForChargeChange = item;

    if (item.deliveryChargeDiscounted != null) {
      this.noDiscountAllowedModal.open();
    } else {
      if (item.deliveryCharge == null) {
        this.canAddDiscount = false;
        this.priceDiscountChecked = false;
        this.manualChargeChecked = true;
      } else {
        this.canAddDiscount = true;
        this.priceDiscountChecked = true;
        this.manualChargeChecked = false;

        const amount = this.itemForChargeChange.deliveryCharge.price;
        this.amountToChange = this.itemForChargeChange.deliveryCharge.price;
        this.editChargeForm.get("discount_amount").setValue(amount);
      }
      this.editTransportChargeModal.open();
    }
  }

  updateDiscountAmount() {
    let discount = this.editChargeForm.value.discount;
    let amount = this.editChargeForm.value.discount_amount;

    discount = discount ? parseInt(discount) : 0;
    amount = parseInt(amount);

    if (discount != 0 && (discount > 15 || discount < 0)) {
      this.discountError = true;
      this.editChargeForm.get("discount_amount").setValue(this.amountToChange);
    } else {
      this.discountError = false;
      if (discount == 0) {
        this.editChargeForm
          .get("discount_amount")
          .setValue(this.amountToChange);
      } else {
        let percentage = discount / 100;
        let discount_amount = (amount - amount * percentage).toFixed(2);

        this.editChargeForm.get("discount_amount").setValue(discount_amount);
      }
    }
  }

  updateTransportCharge() {
    let payload = {};

    if (!this.discountError) {
      if (this.priceDiscountChecked) {
        payload = {
          name: "",
          discount: this.editChargeForm.value.discount,
          discount_amount: this.editChargeForm.value.discount_amount,
        };
      }

      if (this.manualChargeChecked) {
        let name =
          this.editChargeForm.value.transport_charge +
          " - " +
          this.editChargeForm.value.transport_location;

        payload = {
          name,
          transport_charge: this.editChargeForm.value.transport_charge,
          transport_location: this.editChargeForm.value.transport_location,
          load_size: this.editChargeForm.value.load_size,
          discount_amount: this.editChargeForm.value.price,
        };
      }

      if (this.itemForChargeChange.deliveryChargeDiscounted) {
        this.updateDiscountTransportCharge(payload);
      } else {
        this.createDiscountTransportCharge(payload);
      }
    }
  }

  createDiscountTransportCharge(payload) {
    this.store
      .select(getCreatedTransportCharge)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((charge) => {
        if (charge) {
          this.toastrService.showSuccessToaster(
            "Success",
            "Transport charge created",
            null
          );

          const payload = {
            delivery_charge_discounted: charge.id,
          };

          this.store.dispatch(
            new UpdateRentalLineItem(payload, this.itemForChargeChange.id)
          );

          this.store
            .select(updateRentalLineItemSuccess)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((lineItem) => {
              if (lineItem) {
                this.toastrService.showSuccessToaster(
                  "Success",
                  "Unit updated",
                  null
                );
                this.editTransportChargeModal.close();

                // if status is new, refresh, else, direct to quote list
                this.initializeState();
                this.resetForms();
              }
            });
        }
      });

    this.store.dispatch(new CreateDiscountedTransportPrice(payload));
  }

  updateDiscountTransportCharge(payload) {
    this.store
      .select(updateDiscountedTransportChargeSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((charge) => {
        if (charge) {
          this.toastrService.showSuccessToaster(
            "Success",
            "Transport charge updated",
            null
          );
        }
      });

    this.store.dispatch(
      new UpdateDiscountedTransportPrice(
        payload,
        this.itemForChargeChange.deliveryChargeDiscounted.id
      )
    );
  }

  applyLineItemDiscount(lineItem) {
    this.unitForDiscount = lineItem;
    this.amountToChange = this.unitForDiscount.rentalOption.monthlyRate;

    if (this.unitForDiscount.discount == null) {
      let amount = this.unitForDiscount.rentalOption.monthlyRate;
      this.editChargeForm.get("discount_amount").setValue(amount);
      this.editLineItemDiscountModal.open();
    } else {
      this.noDiscountAllowedModal.open();
    }
  }

  updateLineItemDiscount() {
    const payload = {
      discount: this.editChargeForm.value.discount,
      discount_amount: this.editChargeForm.value.discount_amount,
    };

    this.store.dispatch(
      new UpdateRentalLineItem(payload, this.unitForDiscount.id)
    );

    this.store
      .select(updateRentalLineItemSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((lineItem) => {
        if (lineItem) {
          this.toastrService.showSuccessToaster(
            "Success",
            "Unit updated",
            null
          );
          this.editLineItemDiscountModal.close();
          this.unitForDiscount = null;
          this.initializeState();
          this.resetForms();
        }
      });
  }

  setDiscount() {
    this.priceDiscountChecked = true;
    this.manualChargeChecked = false;
  }

  setManualCharge() {
    this.priceDiscountChecked = false;
    this.manualChargeChecked = true;
  }

  getPerfectMatch(unitWidth, unitLength) {
    let _id = null;

    for (let i = 0; i < this.transportLoads.length; i++) {
      let loadSize = this.transportLoads[i].size;

      let length = loadSize.split("m x")[0].replace(/\s/g, "");
      let width = loadSize.split("x")[1].replace(/\s/g, "").charAt(0);
      length = parseInt(length);
      width = parseInt(width);

      if (unitWidth == width && unitLength == length) {
        _id = this.transportLoads[i].id;
        break;
      }
    }

    return _id;
  }

  findClosestMatch(unitWidth, unitLength) {
    let _id = null;
    let matchedItems = [];

    for (let i = 0; i < this.transportLoads.length; i++) {
      let loadSize = this.transportLoads[i].size;

      let length = loadSize.split("m x")[0].replace(/\s/g, "");
      let width = loadSize.split("x")[1].replace(/\s/g, "").charAt(0);
      length = parseInt(length);
      width = parseInt(width);

      if (unitLength <= length && unitWidth <= width) {
        matchedItems.push(this.transportLoads[i]);
      }
    }

    if (matchedItems.length > 0) {
      matchedItems.sort((a, b) => (a.size > b.size ? 1 : -1));
      _id = matchedItems[0].id;
    }

    return _id;
  }

  validateMinMaxMonths() {
    const _id = this.rentalPeriods.findIndex(
      (obj) => obj.id == this.selectedPeriod
    );
    const period = this.rentalPeriods[_id].period;
    const actual = this.rentalForm.value.rental_months;

    let isMaxMonths = period.search("24");

    if (isMaxMonths == 0) {
      if (actual < 24) {
        this.rentalMonthsError = true;
        this.rentalMonthsErrorMsg = `Actual months must be at least 24`;
      } else {
        this.rentalMonthsError = false;
        this.rentalMonthsErrorMsg = "";
      }
    } else {
      let min = period.split("-")[0].replace(/\s/g, "");
      let max = period.split("-")[1].replace(/\s/g, "");
      min = parseInt(min);
      max = parseInt(max);

      if (actual == "" || actual < min || actual > max) {
        this.rentalMonthsError = true;
        this.rentalMonthsErrorMsg = `Actual months must be between ${min} and ${max}`;
      } else {
        this.rentalMonthsError = false;
        this.rentalMonthsErrorMsg = "";
      }
    }
  }

  validateLimitation(value) {
    if (this.selectedProduct && this.selectedProductId) {
      const productLimitation = parseInt(this.selectedProduct.limitation);

      if (productLimitation > 0) {
        let limitError = value > productLimitation ? true : false;

        if (limitError) {
          this.limitationError = true;
          this.limitation = productLimitation;
        } else {
          this.limitationError = false;
          this.limitation = "";
        }
      } else {
        this.limitationError = false;
        this.limitation = "";
      }
    }
  }

  setUnitQty() {
    this.unitQty =
      this.rentalForm.value.unit_qty != "" && this.rentalForm.value.unit_qty > 0
        ? this.rentalForm.value.unit_qty
        : 1;
    this.qtyEntered = this.unitQty > 0 ? true : false;
  }

  addAnotherTruck() {
    this.addSecondTransportLoad = true;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.cancelationSubscription.unsubscribe();
  }
}
